import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Eye, Heart, MessageCircle, Repeat2, Quote, Users,
  Loader2, CheckCircle2, Clock, CalendarClock, RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import adminClient from '@/lib/api/adminClient';

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

interface ThreadsOverview {
  queue: {
    total: number;
    posted: number;
    pending: number;
    future: number;
  };
  profile: {
    id?: string;
    username?: string;
    threads_profile_picture_url?: string;
    threads_biography?: string;
  };
  insights: {
    views?: number | number;
    likes?: number;
    replies?: number;
    reposts?: number;
    quotes?: number;
    followers_count?: number;
  };
}

interface ThreadPost {
  id: string;
  status: 'posted' | 'pending' | 'future';
  category: string;
  publish_after: string;
  text: string;
  thread_id?: string;
  posted_at?: string;
  insights?: {
    views?: number;
    likes?: number;
    replies?: number;
    reposts?: number;
    quotes?: number;
  };
}

interface ThreadsPostsResponse {
  total: number;
  page: number;
  per_page: number;
  items: ThreadPost[];
}

interface UntrackedPost {
  thread_id: string;
  text: string;
  posted_at: string;
  media_type: string;
  insights?: {
    views?: number;
    likes?: number;
    replies?: number;
    reposts?: number;
    quotes?: number;
  };
}

interface UntrackedResponse {
  items: UntrackedPost[];
  total: number;
}

// ═══════════════════════════════════════════════════════════════
// Stat Card
// ═══════════════════════════════════════════════════════════════

function StatCard({
  label, value, icon: Icon, color, loading,
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color?: string;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin mt-2 text-muted-foreground" />
            ) : (
              <p className="text-2xl font-bold mt-1">{value}</p>
            )}
          </div>
          <div className={`w-9 h-9 rounded-lg bg-muted flex items-center justify-center ${color ?? ''}`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════
// Status badge helper
// ═══════════════════════════════════════════════════════════════

const STATUS_CONFIG: Record<string, { label: string; variant: 'success' | 'warning' | 'secondary' }> = {
  posted: { label: '已發佈', variant: 'success' },
  pending: { label: '待發佈', variant: 'warning' },
  future: { label: '排程中', variant: 'secondary' },
};

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_CONFIG[status] ?? { label: status, variant: 'secondary' as const };
  return <Badge variant={config.variant}>{config.label}</Badge>;
}

// ═══════════════════════════════════════════════════════════════
// Main Page
// ═══════════════════════════════════════════════════════════════

export function ThreadsMonitorPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');

  // ── Overview query ────────────────────────────────────────
  const {
    data: overview,
    isLoading: overviewLoading,
    refetch: refetchOverview,
  } = useQuery<ThreadsOverview>({
    queryKey: ['admin', 'threads', 'overview'],
    queryFn: () => adminClient.get('/admin/threads/overview').then((r) => r.data),
    staleTime: 60_000,
  });

  // ── Posts query ───────────────────────────────────────────
  const {
    data: postsData,
    isLoading: postsLoading,
    refetch: refetchPosts,
  } = useQuery<ThreadsPostsResponse>({
    queryKey: ['admin', 'threads', 'posts', page, statusFilter],
    queryFn: () =>
      adminClient
        .get('/admin/threads/posts', {
          params: {
            page,
            per_page: 15,
            status_filter: statusFilter !== 'all' ? statusFilter : undefined,
          },
        })
        .then((r) => r.data),
    staleTime: 30_000,
  });

  // ── Untracked (manual) posts query ──────────────────────
  const {
    data: untrackedData,
    isLoading: untrackedLoading,
    refetch: refetchUntracked,
  } = useQuery<UntrackedResponse>({
    queryKey: ['admin', 'threads', 'untracked'],
    queryFn: () => adminClient.get('/admin/threads/untracked').then((r) => r.data),
    staleTime: 60_000,
  });

  const q = overview?.queue;
  const ins = overview?.insights;
  const profile = overview?.profile;
  const totalPages = postsData ? Math.ceil(postsData.total / 15) : 1;

  return (
    <div className="space-y-6">
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {profile?.threads_profile_picture_url && (
            <img
              src={profile.threads_profile_picture_url}
              alt="avatar"
              className="w-10 h-10 rounded-full"
            />
          )}
          <div>
            <h1 className="text-2xl font-bold">Threads 發文監控</h1>
            {profile?.username && (
              <p className="text-sm text-muted-foreground">@{profile.username}</p>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { refetchOverview(); refetchPosts(); refetchUntracked(); }}
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          重新整理
        </Button>
      </div>

      {/* ── KPI Cards ───────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard
          label="粉絲"
          value={ins?.followers_count ?? '-'}
          icon={Users}
          color="text-blue-500"
          loading={overviewLoading}
        />
        <StatCard
          label="瀏覽 (昨日)"
          value={ins?.views ?? '-'}
          icon={Eye}
          color="text-green-500"
          loading={overviewLoading}
        />
        <StatCard
          label="讚"
          value={ins?.likes ?? 0}
          icon={Heart}
          color="text-pink-500"
          loading={overviewLoading}
        />
        <StatCard
          label="已發佈"
          value={q ? `${q.posted}/${q.total}` : '-'}
          icon={CheckCircle2}
          color="text-emerald-500"
          loading={overviewLoading}
        />
        <StatCard
          label="待發佈"
          value={q?.pending ?? '-'}
          icon={Clock}
          color="text-amber-500"
          loading={overviewLoading}
        />
        <StatCard
          label="排程中"
          value={q?.future ?? '-'}
          icon={CalendarClock}
          color="text-violet-500"
          loading={overviewLoading}
        />
      </div>

      {/* ── Posts Table ──────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">排程貼文</CardTitle>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[130px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                <SelectItem value="posted">已發佈</SelectItem>
                <SelectItem value="pending">待發佈</SelectItem>
                <SelectItem value="future">排程中</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {postsLoading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[70px]">ID</TableHead>
                    <TableHead className="w-[70px]">狀態</TableHead>
                    <TableHead className="w-[90px]">分類</TableHead>
                    <TableHead className="w-[140px]">排程時間</TableHead>
                    <TableHead>內容預覽</TableHead>
                    <TableHead className="w-[60px] text-center">
                      <Eye className="w-3.5 h-3.5 inline" />
                    </TableHead>
                    <TableHead className="w-[50px] text-center">
                      <Heart className="w-3.5 h-3.5 inline" />
                    </TableHead>
                    <TableHead className="w-[50px] text-center">
                      <MessageCircle className="w-3.5 h-3.5 inline" />
                    </TableHead>
                    <TableHead className="w-[50px] text-center">
                      <Repeat2 className="w-3.5 h-3.5 inline" />
                    </TableHead>
                    <TableHead className="w-[50px] text-center">
                      <Quote className="w-3.5 h-3.5 inline" />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(postsData?.items ?? []).map((post) => (
                    <TableRow key={post.id}>
                      <TableCell className="font-mono text-xs">{post.id}</TableCell>
                      <TableCell><StatusBadge status={post.status} /></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{post.category}</TableCell>
                      <TableCell className="text-xs">
                        {post.publish_after
                          ? new Date(post.publish_after).toLocaleString('zh-TW', {
                              month: '2-digit', day: '2-digit',
                              hour: '2-digit', minute: '2-digit',
                              timeZone: 'Asia/Taipei',
                            })
                          : '-'}
                      </TableCell>
                      <TableCell className="max-w-[300px] truncate text-sm">
                        {post.text.replace(/\n/g, ' ').slice(0, 80)}
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {post.insights?.views ?? '-'}
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {post.insights?.likes ?? '-'}
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {post.insights?.replies ?? '-'}
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {post.insights?.reposts ?? '-'}
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {post.insights?.quotes ?? '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!postsData?.items || postsData.items.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                        沒有符合條件的貼文
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-xs text-muted-foreground">
                    共 {postsData?.total ?? 0} 篇，第 {page}/{totalPages} 頁
                  </p>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      上一頁
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      下一頁
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Untracked (Manual) Posts ───────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">手動貼文（近 7 天）</CardTitle>
            <Badge variant="secondary">{untrackedData?.total ?? 0} 篇</Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {untrackedLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (untrackedData?.items?.length ?? 0) === 0 ? (
            <div className="text-center text-muted-foreground py-8 text-sm">
              近 7 天沒有手動發布的貼文
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]">發布時間</TableHead>
                  <TableHead>內容預覽</TableHead>
                  <TableHead className="w-[60px] text-center">
                    <Eye className="w-3.5 h-3.5 inline" />
                  </TableHead>
                  <TableHead className="w-[50px] text-center">
                    <Heart className="w-3.5 h-3.5 inline" />
                  </TableHead>
                  <TableHead className="w-[50px] text-center">
                    <MessageCircle className="w-3.5 h-3.5 inline" />
                  </TableHead>
                  <TableHead className="w-[50px] text-center">
                    <Repeat2 className="w-3.5 h-3.5 inline" />
                  </TableHead>
                  <TableHead className="w-[50px] text-center">
                    <Quote className="w-3.5 h-3.5 inline" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {untrackedData!.items.map((post) => (
                  <TableRow key={post.thread_id}>
                    <TableCell className="text-xs">
                      {post.posted_at
                        ? new Date(post.posted_at).toLocaleString('zh-TW', {
                            month: '2-digit', day: '2-digit',
                            hour: '2-digit', minute: '2-digit',
                            timeZone: 'Asia/Taipei',
                          })
                        : '-'}
                    </TableCell>
                    <TableCell className="max-w-[400px] truncate text-sm">
                      {post.text.replace(/\n/g, ' ').slice(0, 100)}
                    </TableCell>
                    <TableCell className="text-center text-sm">
                      {post.insights?.views ?? '-'}
                    </TableCell>
                    <TableCell className="text-center text-sm">
                      {post.insights?.likes ?? '-'}
                    </TableCell>
                    <TableCell className="text-center text-sm">
                      {post.insights?.replies ?? '-'}
                    </TableCell>
                    <TableCell className="text-center text-sm">
                      {post.insights?.reposts ?? '-'}
                    </TableCell>
                    <TableCell className="text-center text-sm">
                      {post.insights?.quotes ?? '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
